import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { Presentation } from './presentation';
import { CurrentOccupation, SuggestOccupation, SuggestOccupationDetail } from '../../../types/personShow';
import { fetchOccupations } from '../../../services/personProfiles'
import { scrollToElementById } from '../../../libs/scrollUtils';
import {
  requiresSalesDetailWithManager,
} from '../../../libs/occupationValidation';

type Props = {
  onChangedBody: (name: string, value: any) => void;
  currentOccupations: CurrentOccupation[] | '';
  target: string;
  onRegisterValidationHandler?: (handler: () => boolean) => void;
};

export const Component: FunctionComponent<Props> = ({
  onChangedBody,
  currentOccupations,
  target,
  onRegisterValidationHandler,
}) => {
  const [suggestOccupations, setSuggestOccupations] = useState<SuggestOccupation[]>([]);
  const [suggestOccupationDetails, setSuggestOccupationDetails] = useState<SuggestOccupationDetail[]>([]);
  const [currentOccupationNames, setCurrentOccupationNames] = useState<string[]>([]);
  const [currentOccupationDetailIds, setCurrentOccupationDetailIds] = useState<number[]>([]);

  useEffect(() => {
    handleFetchOccupations();
    if (currentOccupations !== '') {
      fetchDefaultData(currentOccupations);
    }
  }, []);

  useEffect(() => {
    if (currentOccupations === '') {
      fetchDefaultData([]);
    }
  }, [currentOccupations]);

  const fetchDefaultData = (occupationsData: CurrentOccupation[]) => {
    const occupations = occupationsData
      .map((item) => (!item.occupation[1] ? item.occupation[0] : null))
      .filter((v): v is string => Boolean(v));

    const details = occupationsData
      .map((item) => item.occupationDetailId)
      .filter((id): id is number => id != null);
    const uniqueOccupations = Array.from(new Set(occupations));

    setCurrentOccupationNames(uniqueOccupations);
    setCurrentOccupationDetailIds(details);

    onChangedBody('currentOccupations', uniqueOccupations);
    onChangedBody('currentOccupationDetails', details);
  };

  const handleFetchOccupations = async () => {
    try {
      const response = await fetchOccupations();
      setSuggestOccupations(response.suggestOccupations);
      setSuggestOccupationDetails(response.suggestOccupationDetails);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (type: 'parent' | 'child', value: string | number) => {
    if (type === 'parent') {
      const exists = currentOccupationNames.includes(value as string);
      const updatedNames = exists
        ? currentOccupationNames.filter((name) => name !== value)
        : [...currentOccupationNames, value as string];

      if (updatedNames.length + currentOccupationDetailIds.length > 3) {
        alert('3職種まで選択できます');
        return;
      }
      setCurrentOccupationNames(updatedNames);
      onChangedBody('currentOccupations', updatedNames);
      onChangedBody('currentOccupationDetails', currentOccupationDetailIds);
    } else {
      const id = value as number;
      const exists = currentOccupationDetailIds.includes(id);
      const updatedDetails = exists
        ? currentOccupationDetailIds.filter((detailId) => detailId !== id)
        : [...currentOccupationDetailIds, id];

      if (updatedDetails.length + currentOccupationNames.length > 3) {
        alert('3職種まで選択できます');
        return;
      }
      setCurrentOccupationDetailIds(updatedDetails);
      onChangedBody('currentOccupations', currentOccupationNames);
      onChangedBody('currentOccupationDetails', updatedDetails);
    }
  };

  const handleScrollTo = (name: string) => {
    scrollToElementById(`flexbox_suggest_${name}`);
  };

  const validateOccupations = useCallback(() => {
    if (!requiresSalesDetailWithManager(currentOccupationDetailIds, suggestOccupationDetails)) {
      return true;
    }

    return false;
  }, [currentOccupationDetailIds, suggestOccupationDetails]);

  useEffect(() => {
    onRegisterValidationHandler?.(validateOccupations);
  }, [onRegisterValidationHandler, validateOccupations]);

  return (
    <Presentation
      title="直近の職種を選択して保存してください"
      limit={3}
      suggestOccupations={suggestOccupations}
      suggestOccupationDetails={suggestOccupationDetails}
      selectedNames={currentOccupationNames}
      selectedDetailIds={currentOccupationDetailIds}
      onClick={handleClick}
      handleScrollTo={handleScrollTo}
    />
  );
};
